import { CHART_SEMANTIC, RAMP_INK } from '../../../components/ui/chart';

export function pct(v: number | null | undefined): string {
  if (v == null) return '—';
  return `${v.toFixed(1)}%`;
}

export function score(v: number | null | undefined): string {
  if (v == null) return '—';
  return `${Math.round(v)}`;
}

export function pos(v: number | null | undefined): string {
  if (v == null) return '—';
  return v.toFixed(1);
}

export const PROVIDER_LABELS: Record<string, string> = {
  openai: 'OpenAI',
  anthropic: 'Claude',
  perplexity: 'Perplexity',
  gemini: 'Gemini',
};

/** Provider dots and stacked cost bars — fixed palette slots so a provider keeps its colour everywhere. */
export const PROVIDER_COLORS: Record<string, string> = {
  // Ink and stone steps: red, amber and green are status colours, never a provider.
  openai: RAMP_INK[0],
  anthropic: RAMP_INK[2],
  perplexity: RAMP_INK[3],
  gemini: RAMP_INK[4],
};

/** Colour for a provider not in {@link PROVIDER_COLORS}. */
export const PROVIDER_FALLBACK_COLOR = CHART_SEMANTIC.neutral;

/**
 * Stable colour for a brand in competitor charts.
 *
 * Our own brand is the one indigo series; competitors hash into the ink/stone
 * steps, so the eye lands on us first. Status colours (red, amber, green) are
 * never used for a brand.
 */
const COMPETITOR_INKS = [RAMP_INK[0], RAMP_INK[1], RAMP_INK[2], RAMP_INK[3]] as const;

export function brandColor(brandId: string, isOwn: boolean): string {
  if (isOwn) return CHART_SEMANTIC.highlight;
  let h = 0;
  for (let i = 0; i < brandId.length; i++) h = (h * 31 + brandId.charCodeAt(i)) >>> 0;
  return COMPETITOR_INKS[h % COMPETITOR_INKS.length]!;
}

export interface DeltaProps {
  value: string;
  trend: 'up' | 'down' | 'neutral';
}

export function deltaProps(
  today: number | null,
  prev: number | null,
  opts?: { lowerIsBetter?: boolean; suffix?: string },
): DeltaProps | undefined {
  if (today == null) return undefined;
  if (prev == null) return { value: 'no baseline', trend: 'neutral' };
  const diff = today - prev;
  if (Math.abs(diff) < 0.05) return { value: '0', trend: 'neutral' };
  const lowerIsBetter = opts?.lowerIsBetter ?? false;
  const trend = lowerIsBetter ? (diff < 0 ? 'up' : 'down') : (diff > 0 ? 'up' : 'down');
  const sign = diff > 0 ? '+' : '';
  return { value: `${sign}${diff.toFixed(1)}${opts?.suffix ?? ''}`, trend };
}

export interface ReportEnvelope {
  columns: string[];
  rows: Array<Array<string | number | null>>;
}

export interface OwnSlice {
  visibility: number | null;
  shareOfVoice: number | null;
  sentiment: number | null;
  position: number | null;
}

export function extractOwn(data: ReportEnvelope | undefined): OwnSlice | null {
  if (!data) return null;
  const isOwnIdx = data.columns.indexOf('is_own');
  const row = data.rows.find((r) => r[isOwnIdx] === 1);
  if (!row) return null;
  return {
    visibility: row[data.columns.indexOf('visibility')] as number | null,
    shareOfVoice: row[data.columns.indexOf('share_of_voice')] as number | null,
    sentiment: row[data.columns.indexOf('sentiment')] as number | null,
    position: row[data.columns.indexOf('position')] as number | null,
  };
}

export function aggregateByGranularity(
  points: { date: string; value: number }[],
  granularity: 'D' | 'W' | 'M',
): { date: string; value: number }[] {
  if (granularity === 'D' || points.length === 0) return points;
  const bucket = new Map<string, { sum: number; n: number; firstDate: string }>();
  for (const p of points) {
    const d = new Date(p.date);
    let key: string;
    if (granularity === 'W') {
      const day = d.getUTCDay();
      const monday = new Date(d);
      monday.setUTCDate(d.getUTCDate() - ((day + 6) % 7));
      key = monday.toISOString().split('T')[0]!;
    } else {
      key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-01`;
    }
    const slot = bucket.get(key) ?? { sum: 0, n: 0, firstDate: key };
    slot.sum += p.value;
    slot.n += 1;
    bucket.set(key, slot);
  }
  return Array.from(bucket.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, s]) => ({ date, value: s.sum / s.n }));
}
